/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable import/no-extraneous-dependencies */
import { useContext, useEffect, useMemo, useState } from 'react';
import { isEmpty } from 'lodash';
import queryString from 'query-string';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { baseCompanyUrl } from 'common/config/urls';
import { WATERFALL_SLUG } from 'common/constants/allocations';
import {
  BREAKPOINT_ANALYSIS_SLUG,
  CAP_TABLE_SLUG,
  CASH_FLOW_LEDGER_SLUG,
  FUND_OWNERSHIP_SLUG,
} from 'common/constants/cap-table';
import { COMPANIES_SUMMARY_SLUG } from 'common/constants/companies';
import { DOCUMENT_GENERATION_JOBS_SLUG, DOCUMENTS_SLUG, } from 'common/constants/documents';
import { FINANCIAL_SLUG } from 'common/constants/financials';
import { MISSING_MD_MESSAGE, MISSING_MD_TITLE } from 'common/constants/messages/validations';
import { EDIT_COMMON_PROFILE, EDIT_COMPANY, EXCEL_EXPORT, PDF_EXPORT } from 'common/constants/pageActions';
import { VERSION_QUERY_PARAM } from 'common/constants/pageFilters';
import { VALUATIONS_SLUG } from 'common/constants/valuations';
import { useExtraPageActionVisibility } from 'common/hooks';
import { useStore } from 'common/store';
import { ExtraAction, NavItems, UseStoreValues } from 'common/types/store';
import EditCommonProfileDialog from 'components/EditCommonProfileDialog';
import MissingMeasurementDateMessage from 'components/MissingMeasurementDateMessage';
import { PdfExportConfigDialog } from 'components/PdfExportConfigDialog';
import withCompany from 'components/WithCompany';
import LayoutContext from 'context/LayoutContext';
import {
  CapTableView,
  Financials as FinancialPage,
  Portfolio as PortfolioView,
  ValuationsAllocation as ValuationsAllocationView,
  Waterfall as WaterfallView,
} from 'pages';
import RequestDocumentsView from 'pages/Documents/RequestDocumentsView';
import { TAB_QUERY_PARAM } from 'pages/ValuationsAllocation/common/constants/valuations';
import { WATERFALL_QUERY_PARAMS } from 'pages/Waterfall/constants';
import { extractDate, removeParamFromQueryParams } from 'utilities';
import { useUserUtils } from 'utilities/userUtils';
import { COMPANY_ROUTES } from './company.routes';
import DocumentGenerationJobs from '../../pages/DocumentGenerationJobs';
import DrawerBottomSpacer from '../Drawer/draggable-drawer/DrawerBottomSpacer';
import useCheckFeatures from '../FeatureEnabler/hooks';

const CompanyContainer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tableSlugParam } = useParams();
  const [storeValues] = useStore() as unknown as UseStoreValues;
  const { companyInfo, firmInfo } = storeValues;
  const {
    setPageTitle,
    setNavItems,
    setShowAlert,
    setAlertMessage,
    setCommonMeasurementDate,
    toggleMainForm,
    toggleExportDialog,
    togglePdfDialog,
    openPdfDialog,
    updateExtraPageActions,
  } = useContext(LayoutContext);
  const [, checkFeature] = useCheckFeatures();
  const { isStaffUser, isSuperUser } = useUserUtils();
  const actionVisibility = useExtraPageActionVisibility({
    pdfExport: isStaffUser() || isSuperUser(),
  });
  const [editProfileOpen, setEditProfileOpen] = useState(false);

  const commonProfileId = companyInfo?.common_profile_id as number | null | undefined;

  const firmInfoSlug = useMemo(() => firmInfo?.slug ?? '', [firmInfo]);
  const companyInfoSlug = useMemo(() => companyInfo?.slug ?? '', [companyInfo]);

  const isLoaded = useMemo(
    () => (!isEmpty(firmInfo) && !isEmpty(companyInfo) ? baseCompanyUrl(firmInfoSlug, companyInfoSlug) : null),
    [firmInfo, companyInfo, firmInfoSlug, companyInfoSlug]
  );

  const parsedQuery = useMemo(() => queryString.parse(location.search), [location]);

  const isDataLoading = useMemo(
    () =>
      // Check if we're still waiting for basic company/firm data
      isEmpty(firmInfo) || isEmpty(companyInfo) || !companyInfo?.id,
    [firmInfo, companyInfo]
  );

  useEffect(() => {
    if (setCommonMeasurementDate && parsedQuery?.date && typeof parsedQuery.date === 'string') {
      const extractedDate = extractDate({
        dateSlug: parsedQuery.date,
      });
      if (extractedDate) {
        setCommonMeasurementDate(extractedDate);
      }
    }
  }, [parsedQuery?.date]);

  const navigateToUrl = (getUrl: (slug: string, companySlug: string) => string) => {
    if (isLoaded) {
      const newPathName = getUrl(firmInfoSlug, companyInfoSlug);

      // Use the measurement date query string if available
      if (parsedQuery.date) {
        // Delete the Version query param if it exists
        if (VERSION_QUERY_PARAM in parsedQuery) {
          delete parsedQuery[VERSION_QUERY_PARAM];
        }

        // Delete the Valuations Approach query param if it exists
        if (TAB_QUERY_PARAM in parsedQuery) {
          delete parsedQuery[TAB_QUERY_PARAM];
        }
      }

      // Clean up waterfall-specific parameters when navigating away from waterfall page
      if (tableSlugParam === WATERFALL_SLUG) {
        const waterfallParams = WATERFALL_QUERY_PARAMS;
        waterfallParams.forEach(param => {
          if (param in parsedQuery) {
            delete parsedQuery[param];
          }
        });
      }

      navigate({ pathname: newPathName, search: queryString.stringify(parsedQuery) });
    }
  };

  const menuItems = useMemo(
    (): NavItems[] =>
      COMPANY_ROUTES.map(route => ({
        ...route,
        callback: () => navigateToUrl(route.route),
      })).filter(route => (route.featureFlag ? checkFeature(route.featureFlag) : true)) as NavItems[],
    [companyInfo, isLoaded, parsedQuery, tableSlugParam]
  );

  // Hides alert box when browsing to another location
  useEffect(
    () => () => {
      setShowAlert?.(false);
      setAlertMessage?.('');
    },
    [location.pathname]
  );

  useEffect(() => {
    if (!isEmpty(companyInfo) && companyInfo.name) {
      setPageTitle?.(companyInfo.name);
      setNavItems?.(menuItems);
    }

    return () => {
      setPageTitle?.(null);
      setNavItems?.(null);
    };
  }, [companyInfo, location]);

  useEffect(() => {
    const extraActions: ExtraAction[] = [
      {
        label: EDIT_COMPANY,
        callback: toggleMainForm,
        isActive: true,
      },
    ];
    if (actionVisibility.excelExport) {
      extraActions.push({
        label: EXCEL_EXPORT,
        callback: toggleExportDialog,
        isActive: Boolean(companyInfo?.measurement_date_id),
      });
    }

    if (actionVisibility.pdfExport) {
      extraActions.push({
        label: PDF_EXPORT,
        callback: togglePdfDialog,
        isActive: true,
      });
    }

    if ((isStaffUser() || isSuperUser()) && commonProfileId != null) {
      extraActions.push({
        label: EDIT_COMMON_PROFILE,
        callback: () => setEditProfileOpen(true),
        isActive: true,
      });
    }

    updateExtraPageActions?.(extraActions);
  }, [
    toggleMainForm,
    companyInfo,
    commonProfileId,
    updateExtraPageActions,
    togglePdfDialog,
    toggleExportDialog,
    actionVisibility.excelExport,
    actionVisibility.pdfExport,
  ]);

  if (!isDataLoading && !isEmpty(companyInfo) && !companyInfo?.measurement_date_id) {
    removeParamFromQueryParams('date');
    return <MissingMeasurementDateMessage title={MISSING_MD_TITLE} tagline={MISSING_MD_MESSAGE} />;
  }

  const renderCurrentView = () => {
    switch (tableSlugParam) {
      case COMPANIES_SUMMARY_SLUG:
        return <PortfolioView />;
      case FINANCIAL_SLUG:
        return <FinancialPage />;
      case CAP_TABLE_SLUG:
      case FUND_OWNERSHIP_SLUG:
      case BREAKPOINT_ANALYSIS_SLUG:
      case CASH_FLOW_LEDGER_SLUG:
        return <CapTableView />;
      case VALUATIONS_SLUG:
        return <ValuationsAllocationView />;
      case WATERFALL_SLUG:
        return <WaterfallView />;
      case DOCUMENTS_SLUG:
        return <RequestDocumentsView />;
      case DOCUMENT_GENERATION_JOBS_SLUG:
        return <DocumentGenerationJobs />;
      default:
        return <p>Hello missing route</p>;
    }
  };

  return (
    // Use reusable wrapper that reserves bottom space equal to the drawer height
    <DrawerBottomSpacer>
      {renderCurrentView()}
      <PdfExportConfigDialog
        open={openPdfDialog}
        onClose={togglePdfDialog}
      />
      {editProfileOpen && commonProfileId != null && (
        <EditCommonProfileDialog
          open={editProfileOpen}
          onClose={() => setEditProfileOpen(false)}
          profileId={commonProfileId}
        />
      )}
    </DrawerBottomSpacer>
  );
};

export default withCompany(CompanyContainer);
